import { InlineFieldInput } from "@/components/inline-field-group";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { ReviewChoiceGroup } from "@/components/review-choice-group";
import type { ShipmentCustomerNotificationPolicy } from "@/lib/shipment-customer-notification-policy";

export function ShipmentCustomerNotificationPolicyFields({
  value,
  onChange,
}: {
  value: ShipmentCustomerNotificationPolicy;
  onChange: (value: ShipmentCustomerNotificationPolicy) => void;
}) {
  const set = <K extends keyof ShipmentCustomerNotificationPolicy>(
    key: K,
    next: ShipmentCustomerNotificationPolicy[K],
  ) => onChange({ ...value, [key]: next });

  return (
    <div className="space-y-5 rounded-lg border bg-muted/20 p-4">
      <div>
        <h4 className="text-sm font-semibold">Customer delivery email</h4>
        <p className="mt-1 text-xs leading-5 text-muted-foreground">
          Required for LTL auto-send. If these decisions are missing or conflict
          with the booking, the email stays in draft for review.
        </p>
      </div>

      <div className="space-y-2">
        <Label>Receiving method</Label>
        <ReviewChoiceGroup
          label="Receiving method"
          value={value.receiving_method}
          onChange={(next) => set("receiving_method", next)}
          options={[
            { value: "", label: "Not confirmed" },
            { value: "liftgate", label: "Liftgate" },
            { value: "customer_unload", label: "Customer unload" },
            { value: "dock_or_forklift", label: "Dock / forklift" },
          ]}
          className="w-full"
        />
        <p className="text-xs text-muted-foreground">
          Customer unload is only safe when every item is 100 lbs or less and no
          handling unit is longer than 6 ft.
        </p>
      </div>

      <div className="space-y-2">
        <Label>Approved damage instructions</Label>
        <ReviewChoiceGroup
          label="Approved damage instructions"
          value={value.damage_policy}
          onChange={(next) => set("damage_policy", next)}
          options={[
            { value: "", label: "Not confirmed" },
            {
              value: "accept_and_note_damage",
              label: "Accept and note damage",
            },
            { value: "refuse_severe_damage", label: "Refuse severe damage" },
          ]}
          className="w-full"
        />
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between gap-4">
          <div>
            <Label htmlFor="customer-email-detention">
              Include carrier free-time table
            </Label>
            <p className="mt-1 text-xs text-muted-foreground">
              Only enable when the carrier confirmed the standard weight bands.
            </p>
          </div>
          <Switch
            id="customer-email-detention"
            checked={value.detention_policy === "standard_weight_bands"}
            onCheckedChange={(checked) =>
              set("detention_policy", checked ? "standard_weight_bands" : "")
            }
          />
        </div>
        {value.detention_policy === "standard_weight_bands" ? (
          <div className="space-y-2">
            <Label htmlFor="customer-email-detention-rate">
              Additional-time rate
            </Label>
            <InlineFieldInput
              id="customer-email-detention-rate"
              value={value.detention_rate}
              onChange={(event) => set("detention_rate", event.target.value)}
              placeholder="$45 per 15 minutes"
            />
          </div>
        ) : null}
      </div>
    </div>
  );
}
